import React, { useState, useMemo, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import ContextCalendar from './ContextCalendar';
import { formatAssignmentDateSpan } from '../../../utils/dateUtils';
import { getResourceColor, getInitials } from '../../../utils/resourceUtils';
import ConflictHoverCard from './ConflictHoverCard';
import AssignmentHoverCard from './AssignmentHoverCard';
import { getAssignmentConflicts, parseConflictString, calculatePopupPosition } from '../../../utils/leaderUtils';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import { useSafeClose } from '../../../hooks/useSafeClose';
import StatusBadge from '../../shared/StatusBadge/StatusBadge';
import 'leaflet/dist/leaflet.css';
import './AssignmentReadOnlyModal.css';

// Component to auto fit bounds of map
function FitBounds({ markers }) {
    const map = useMap();
    useEffect(() => {
        if (markers && markers.length > 0) {
            const valid = markers.filter(m => m && typeof m[0] === 'number' && typeof m[1] === 'number');
            if (valid.length > 0) {
                const bounds = L.latLngBounds(valid);
                map.fitBounds(bounds, { padding: [50, 50], maxZoom: 15 });
            }
        }
    }, [markers, map]);
    return null;
}

/**
 * Premium Read-Only Modal for Rank 2 users.
 * Displays assignment details in a compact, two-column centered layout.
 * 
 * @param {Object} props
 */
const AssignmentReadOnlyModal = ({ 
    assignment, 
    isOpen, 
    onClose, 
    workers = [], 
    vehicles = [],
    assignmentsForConflicts = [],
    absencesForConflicts = [],
    kolonnen = [],
    currentWorkerId,
    isConflictDetectionEnabled = true,
    companyLoc = {},
    onAssignmentClick,
    onGoBack,
    onGoForward,
    editStack = [],
    forwardStack = [],
    onExport
}) => {
    const [hoveredResourceId, setHoveredResourceId] = useState(null);
    const [hoveredKolonneId, setHoveredKolonneId] = useState(null);
    const [hoveredAssignment, setHoveredAssignment] = useState(null);
    const [hoveredAssignmentConflicts, setHoveredAssignmentConflicts] = useState(null);
    const [hoverPosition, setHoverPosition] = useState(null);
    const hoverTimeoutRef = useRef(null);
    const mapRef = useRef(null);
    const { overlayRef, onMouseDown, onMouseUp } = useSafeClose(onClose);

    // Initialisierung und Resize-Handling
    useEffect(() => {
        return () => {
            if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
        };
    }, []);

    // Conflict Detection
    const conflicts = useMemo(() => {
        if (!isConflictDetectionEnabled || !assignment) return { total: 0 };
        return getAssignmentConflicts(assignment, assignmentsForConflicts, absencesForConflicts);
    }, [assignment, assignmentsForConflicts, absencesForConflicts, isConflictDetectionEnabled]);

    // Team/Kolonnen Conflicts (Logic like in AssignmentHoverCard)
    const kolonneConflicts = useMemo(() => {
        if (!isOpen || !assignment || !isConflictDetectionEnabled || !conflicts || conflicts.total === 0) return [];
        
        // Need to recreate kolonnenMap here since it's used inside useMemo but defined later? 
        // Actually, let's keep it simple and just move the hook while keeping its logic.
        // Wait, kolonnenMap is defined AFTER the early return. 
        // A better approach is to define all data-preprocessing AFTER hooks but BEFORE early returns if possible, 
        // OR pass necessary data to useMemo.
        
        const kMap = {};
        (assignment.workers || []).forEach(aw => {
            if (aw.kolonne_id) {
                if (!kMap[aw.kolonne_id]) kMap[aw.kolonne_id] = { members: [] };
                kMap[aw.kolonne_id].members.push({ ...aw, type: 'worker' });
            }
        });
        (assignment.vehicles || []).forEach(av => {
            if (av.kolonne_id) {
                if (!kMap[av.kolonne_id]) kMap[av.kolonne_id] = { members: [] };
                kMap[av.kolonne_id].members.push({ ...av, type: 'vehicle' });
            }
        });

        const blocks = [];
        Object.entries(kMap).forEach(([kid, kData]) => {
            const allMemberIds = kData.members.map(m => String(m.worker_id || m.vehicle_id));
            if (allMemberIds.length === 0) return;

            const firstId = allMemberIds[0];
            const firstRes = kData.members[0];
            const firstIdConfs = (firstRes.type === 'worker' ? conflicts.workers?.[firstId] : conflicts.vehicles?.[firstId]) || [];

            const commonMsgs = firstIdConfs.filter(msg => {
                if (!msg.includes('Mehrfachbelegung') && !msg.includes('Doppelbelegung')) return false;
                return allMemberIds.every(id => {
                    const member = kData.members.find(m => String(m.worker_id || m.vehicle_id) === String(id));
                    const c = (member.type === 'worker' ? conflicts.workers?.[id] : conflicts.vehicles?.[id]) || [];
                    return c.includes(msg);
                });
            });

            if (commonMsgs.length > 0) {
                const kMaster = kolonnen.find(k => String(k.id) === String(kid));
                const kName = kMaster?.name || 
                              (assignment.workers.find(w => String(w.kolonne_id) === String(kid))?.kolonne_name) || 
                              (assignment.vehicles.find(v => String(v.kolonne_id) === String(kid))?.kolonne_name) || 
                              kMaster?.code || 'Unbenannt';
                blocks.push({ kolonneId: kid, name: kName, messages: commonMsgs });
            }
        });
        return blocks;
    }, [isOpen, assignment, conflicts, isConflictDetectionEnabled]);
    if (!isOpen || !assignment) return null;

    const projectTitle = assignment.project_name || '(Unbenanntes Projekt)';
    const isAllDay = assignment.all_day || assignment.is_all_day;
    const dateStr = formatAssignmentDateSpan(assignment);

    // Group resources by Kolonne
    const kolonnenMap = {};
    const individualResources = { workers: [], vehicles: [] };

    (assignment.workers || []).forEach(aw => {
        const worker = workers.find(w => String(w.id) === String(aw.worker_id));
        const conflict = conflicts?.workers?.[aw.worker_id];
        const resourceObj = { ...aw, ...worker, type: 'worker', hasConflict: !!conflict, conflictData: conflict };
        
        if (aw.kolonne_id) {
            if (!kolonnenMap[aw.kolonne_id]) {
                const kMaster = kolonnen.find(k => String(k.id) === String(aw.kolonne_id));
                kolonnenMap[aw.kolonne_id] = { 
                    id: aw.kolonne_id,
                    name: kMaster?.name || aw.kolonne_name || kMaster?.code || 'Unbenannt', 
                    members: [] 
                };
            }
            kolonnenMap[aw.kolonne_id].members.push(resourceObj);
        } else {
            individualResources.workers.push(resourceObj);
        }
    });

    (assignment.vehicles || []).forEach(av => {
        const vehicle = vehicles.find(v => String(v.id) === String(av.vehicle_id));
        const conflict = conflicts?.vehicles?.[av.vehicle_id];
        const resourceObj = { ...av, ...vehicle, type: 'vehicle', hasConflict: !!conflict, conflictData: conflict };

        if (av.kolonne_id) {
            if (!kolonnenMap[av.kolonne_id]) {
                const kMaster = kolonnen.find(k => String(k.id) === String(av.kolonne_id));
                kolonnenMap[av.kolonne_id] = { 
                    id: av.kolonne_id,
                    name: kMaster?.name || av.kolonne_name || kMaster?.code || 'Unbenannt', 
                    members: [] 
                };
            }
            kolonnenMap[av.kolonne_id].members.push(resourceObj);
        } else {
            individualResources.vehicles.push(resourceObj);
        }
    });

    const handleGoogleMapsOpen = () => {
        const points = [];
        const startAddr = assignment.start_address || companyLoc?.address;
        const returnAddr = assignment.return_address || assignment.start_address || companyLoc?.address;

        if (startAddr) points.push(startAddr);
        if (assignment.location) points.push(assignment.location);
        // Force 3 points if we have a start destination and return (even if return is same as start)
        if (returnAddr) points.push(returnAddr);
        
        if (points.length < 2) return;
        
        const origin = encodeURIComponent(points[0]);
        const destination = encodeURIComponent(points[points.length - 1]);
        const waypoints = points.slice(1, -1).map(p => encodeURIComponent(p)).join('|');
        
        const url = `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${destination}${waypoints ? `&waypoints=${waypoints}` : ''}&travelmode=driving`;
        window.open(url, '_blank');
    };

    const handleResourceHover = (e, resource) => {
        if (!resource.hasConflict) return;
        if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);

        const rect = e.currentTarget.getBoundingClientRect();
        setHoveredResourceId(resource.id || resource.worker_id || resource.vehicle_id);
        
        const position = calculatePopupPosition(rect, 320, 400, null, { gap: 12 });
        setHoverPosition(position);
    };

    const handleResourceLeave = () => {
        if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
        hoverTimeoutRef.current = setTimeout(() => {
            setHoveredResourceId(null);
            setHoveredKolonneId(null);
            setHoveredAssignment(null);
            setHoveredAssignmentConflicts(null);
            setHoverPosition(null);
        }, 400); // 400ms bridge delay
    };

    const handleAssignmentHover = (a, e) => {
        if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
        const rect = e.currentTarget.getBoundingClientRect();
        
        // Calculate conflicts for the hovered assignment from context
        const conflicts = getAssignmentConflicts(a, assignmentsForConflicts, [], vehicles);
        
        const position = calculatePopupPosition(rect, 340, 480, null, { gap: 12 });
        setHoveredAssignment(a);
        setHoveredAssignmentConflicts(conflicts);
        setHoverPosition(position);
    };

    const handleAssignmentLeave = () => {
        handleResourceLeave();
    };

    const handleKolonneHover = (e, kid) => {
        const kConflict = kolonneConflicts.find(kc => String(kc.kolonneId) === String(kid));
        if (!kConflict) return;
        
        if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);

        const rect = e.currentTarget.getBoundingClientRect();
        setHoveredKolonneId(kid);
        
        const position = calculatePopupPosition(rect, 320, 400, null, { gap: 12 });
        setHoverPosition(position);
    };

    const handlePopupEnter = () => {
        if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    };

    // Prepare map markers
    const markers = [];
    if (assignment) {
        const sLat = assignment.route_from_lat || assignment.start_lat || (assignment.start_address ? null : companyLoc?.lat);
        const sLng = assignment.route_from_lng || assignment.start_lng || (assignment.start_address ? null : companyLoc?.lng);
        if (sLat && sLng) markers.push({ lat: sLat, lng: sLng, label: 'Start' });

        const dLat = assignment.route_to_lat || assignment.lat;
        const dLng = assignment.route_to_lng || assignment.lng;
        if (dLat && dLng) markers.push({ lat: dLat, lng: dLng, label: 'Einsatzort' });
        
        const rLat = assignment.route_return_lat || assignment.return_lat || (assignment.return_address ? null : (assignment.start_address ? assignment.start_lat : companyLoc?.lat));
        const rLng = assignment.route_return_lng || assignment.return_lng || (assignment.return_address ? null : (assignment.start_address ? assignment.start_lng : companyLoc?.lng));
        if (rLat && rLng) markers.push({ lat: rLat, lng: rLng, label: 'Rückkehr' });
    }

    return createPortal(
        <div className="ro-modal-backdrop" ref={overlayRef} onMouseDown={onMouseDown} onMouseUp={onMouseUp}>
            <div className="ro-modal-container" onClick={(e) => e.stopPropagation()}>
                <div className="ro-modal-header">
                    <div className="ro-header-left">
                        <div className="ro-nav-controls">
                            <button 
                                className={`ro-nav-btn ${editStack.length === 0 ? 'disabled' : ''}`} 
                                onClick={onGoBack} 
                                disabled={editStack.length === 0}
                                title="Zurück zum vorherigen Einsatz"
                            >
                                ‹
                            </button>
                            <button 
                                className={`ro-nav-btn ${forwardStack.length === 0 ? 'disabled' : ''}`} 
                                onClick={onGoForward} 
                                disabled={forwardStack.length === 0}
                                title="Vor zum nächsten Einsatz"
                            >
                                ›
                            </button>
                        </div>
                        <div className="ro-header-title">
                            <span className="ro-header-icon">✨</span>
                            <h2>Einsatz Details</h2>
                        </div>
                    </div>
                    <div className="ro-header-right">
                        <button 
                            className="ro-export-shortcut" 
                            onClick={() => onExport?.(assignment.id)}
                            title="Diesen Einsatz als PDF exportieren"
                            style={{ 
                                background: '#f1f5f9', 
                                border: 'none', 
                                borderRadius: '8px', 
                                padding: '6px 12px',
                                fontSize: '0.8rem',
                                fontWeight: '600',
                                color: '#334155',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                marginRight: '12px'
                            }}
                        >
                            📄 PDF Export
                        </button>
                        <StatusBadge 
                            status={assignment.status || 'aktiv'} 
                            className="ro-status-badge" 
                        />
                        <button className="ro-close-btn" onClick={onClose}>✕</button>
                    </div>
                </div>

                <div className="ro-modal-body">
                    {/* LEFT COLUMN: DETAILS */}
                    <div className="ro-details-column">
                        <h1 className="ro-project-title">{projectTitle}</h1>
                        
                        <div className="ro-info-grid">
                            <div className="ro-info-item">
                                <label>📍 Einsatzort</label>
                                <div className="ro-info-value">{assignment.location || '(keine Angabe)'}</div>
                            </div>
                            <div className="ro-info-item">
                                <label>🕒 Zeitraum</label>
                                <div className="ro-info-value">{dateStr}</div>
                            </div>
                        </div>


                        <div className="ro-section">
                            <label className="ro-section-label">👥 Mitarbeiter & Fahrzeuge</label>
                            <div className="ro-resources-list">
                                {Object.keys(kolonnenMap).map(kid => (
                                    <div key={kid} className="ro-kolonne-group">
                                        <div 
                                            className={`ro-kolonne-name ${kolonneConflicts.find(kc => String(kc.kolonneId) === String(kid)) ? 'has-conflict' : ''}`}
                                            onMouseEnter={(e) => handleKolonneHover(e, kid)}
                                            onMouseLeave={handleResourceLeave}
                                        >
                                            Kolonne: {kolonnenMap[kid].name}
                                            {kolonneConflicts.find(kc => String(kc.kolonneId) === String(kid)) && (
                                                <span className="ro-conflict-icon">⚠️</span>
                                            )}
                                        </div>
                                        <div className="ro-resource-chips">
                                            {kolonnenMap[kid].members.map((m, idx) => (
                                                <div 
                                                    key={idx} 
                                                    className={`ro-resource-chip ${m.hasConflict ? 'has-conflict' : ''}`}
                                                    onMouseEnter={(e) => handleResourceHover(e, m)}
                                                    onMouseLeave={handleResourceLeave}
                                                >
                                                    {m.type === 'worker' ? (
                                                        <>
                                                            <div className="ro-chip-avatar" style={{ background: getResourceColor(m) }}>
                                                                {getInitials(m.name || m.worker_name)}
                                                            </div>
                                                            <span>{m.name || m.worker_name}</span>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <span className="ro-chip-icon">🚚</span>
                                                            <span>{m.license_plate || m.internal_name || 'Fahrzeug'}</span>
                                                        </>
                                                    )}
                                                    {m.hasConflict && <span className="ro-conflict-icon">⚠️</span>}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}

                                {(individualResources.workers.length > 0 || individualResources.vehicles.length > 0) && (
                                    <div className="ro-kolonne-group">
                                        <div className="ro-kolonne-name">Einzelressourcen</div>
                                        <div className="ro-resource-chips">
                                            {individualResources.workers.map((m, idx) => (
                                                <div 
                                                    key={idx} 
                                                    className={`ro-resource-chip ${m.hasConflict ? 'has-conflict' : ''}`}
                                                    onMouseEnter={(e) => handleResourceHover(e, m)}
                                                    onMouseLeave={handleResourceLeave}
                                                >
                                                    <div className="ro-chip-avatar" style={{ background: getResourceColor(m) }}>
                                                        {getInitials(m.name || m.worker_name)}
                                                    </div>
                                                    <span>{m.name || m.worker_name}</span>
                                                    {m.hasConflict && <span className="ro-conflict-icon">⚠️</span>}
                                                </div>
                                            ))}
                                            {individualResources.vehicles.map((m, idx) => (
                                                <div 
                                                    key={idx} 
                                                    className={`ro-resource-chip ${m.hasConflict ? 'has-conflict' : ''}`}
                                                    onMouseEnter={(e) => handleResourceHover(e, m)}
                                                    onMouseLeave={handleResourceLeave}
                                                >
                                                    <span className="ro-chip-icon">🚚</span>
                                                    <span>{m.license_plate || m.internal_name || 'Fahrzeug'}</span>
                                                    {m.hasConflict && <span className="ro-conflict-icon">⚠️</span>}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Route Section */}
                        <div className="ro-section">
                            <details className="ro-expandable-section">
                                <summary className="ro-section-label">🗺️ Route & Fahrzeit</summary>
                                <div className="ro-route-content">
                                    <div className="ro-route-addresses">
                                        <div className="ro-addr"><strong>Start:</strong> {assignment.start_address || companyLoc?.address || '(nicht angegeben)'}</div>
                                        <div className="ro-addr"><strong>Ziel:</strong> {assignment.location || '(nicht angegeben)'}</div>
                                        <div className="ro-addr"><strong>Rückfahrt:</strong> {assignment.return_address || assignment.start_address || companyLoc?.address || '(wie Start)'}</div>
                                    </div>

                                    <div className="ro-info-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
                                        <div className="ro-info-item">
                                            <label>⏱️ Hinfahrt</label>
                                            <div className="ro-info-value">{assignment.travel_time_to ? `${Math.floor(assignment.travel_time_to / 60)}h ${String(assignment.travel_time_to % 60).padStart(2, '0')}m` : '(n.v.)'}</div>
                                        </div>
                                        <div className="ro-info-item">
                                            <label>⏱️ Rückfahrt</label>
                                            <div className="ro-info-value">{assignment.travel_time_return ? `${Math.floor(assignment.travel_time_return / 60)}h ${String(assignment.travel_time_return % 60).padStart(2, '0')}m` : '(n.v.)'}</div>
                                        </div>
                                        <div className="ro-info-item">
                                            <label>⏱️ Arbeitszeit</label>
                                            <div className="ro-info-value">
                                                {assignment.work_duration && assignment.work_duration > 0 
                                                    ? `${Math.floor(assignment.work_duration / 60)}h ${String(assignment.work_duration % 60).padStart(2, '0')}m` 
                                                    : '(nicht angegeben)'}
                                            </div>
                                        </div>
                                    </div>

                                    {markers.length > 0 && (
                                        <div className="ro-map-preview">
                                            <MapContainer 
                                                style={{ height: '200px', width: '100%', borderRadius: '12px' }} 
                                                zoom={13} 
                                                scrollWheelZoom={true}
                                                zoomControl={true}
                                            >
                                                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                                                {markers.map((m, i) => (
                                                    <Marker key={i} position={[m.lat, m.lng]}>
                                                        <Popup>{m.label}</Popup>
                                                    </Marker>
                                                ))}
                                                <FitBounds markers={markers.map(m => [m.lat, m.lng])} />
                                            </MapContainer>
                                        </div>
                                    )}

                                    <button className="ro-maps-btn" onClick={handleGoogleMapsOpen}>
                                        🗺️ Google Maps
                                    </button>
                                </div>
                            </details>
                        </div>

                        <div className="ro-section">
                            <label className="ro-section-label">📝 Notizen</label>
                            <div className="ro-notes-box">
                                {assignment.notes || '(keine Notizen vorhanden)'}
                            </div>
                        </div>
                    </div>

                    {/* RIGHT COLUMN: CONTEXT CALENDAR */}
                    <div className="ro-context-column">
                        <ContextCalendar 
                            startDate={assignment.start_datetime ? assignment.start_datetime.split(/[T ]/)[0] : new Date().toISOString().split('T')[0]}
                            endDate={assignment.end_datetime ? assignment.end_datetime.split(/[T ]/)[0] : new Date().toISOString().split('T')[0]}
                            currentStartTime={assignment.start_datetime ? assignment.start_datetime.split(/[T ]/)[1]?.substring(0, 5) : '00:00'}
                            currentEndTime={assignment.end_datetime ? assignment.end_datetime.split(/[T ]/)[1]?.substring(0, 5) : '23:59'}
                            isAllDay={isAllDay}
                            currentAssignmentId={assignment.id}
                            onAssignmentClick={onAssignmentClick}
                            filterByWorkerId={currentWorkerId}
                            workers={workers}
                            vehicles={vehicles}
                            assignmentsForConflicts={assignmentsForConflicts}
                            isConflictDetectionEnabled={isConflictDetectionEnabled}
                            kolonnen={kolonnen}
                            onAssignmentHover={handleAssignmentHover}
                            onAssignmentHoverEnd={handleAssignmentLeave}
                            hideNavigation={true}
                        />
                    </div>
                </div>
            </div>

            {/* Hover Card Portal */}
            {(hoveredResourceId || hoveredKolonneId) && hoverPosition && (
                <ConflictHoverCard
                    title={(() => {
                        if (hoveredKolonneId) {
                            return `Kolonne: ${kolonnenMap[hoveredKolonneId]?.name || 'Unbenannt'}`;
                        }
                        const w = workers.find(w => String(w.id) === String(hoveredResourceId));
                        if (w) return w.name;
                        const v = vehicles.find(v => String(v.id) === String(hoveredResourceId));
                        if (v) return v.license_plate || v.internal_name;
                        return 'Unbekannt';
                    })()}
                    conflict={{
                        blocks: (() => {
                            if (hoveredKolonneId) {
                                const kc = kolonneConflicts.find(c => String(c.kolonneId) === String(hoveredKolonneId));
                                return (kc?.messages || []).map(m => parseConflictString(m)).filter(Boolean);
                            }
                            const msgs = (conflicts?.workers?.[hoveredResourceId]) || (conflicts?.vehicles?.[hoveredResourceId]) || [];
                            return msgs.map(m => parseConflictString(m)).filter(Boolean);
                        })()
                    }}
                    position={{
                        ...hoverPosition,
                        zIndex: 9999
                    }}
                    onMouseEnter={handlePopupEnter} 
                    onMouseLeave={handleResourceLeave}
                />
            )}

            {hoveredAssignment && hoverPosition && (
                <AssignmentHoverCard 
                    assignment={hoveredAssignment}
                    conflicts={hoveredAssignmentConflicts}
                    position={{
                        ...hoverPosition,
                        zIndex: 9999
                    }}
                    workers={workers}
                    vehicles={vehicles}
                    kolonnen={kolonnen}
                    isConflictDetectionEnabled={isConflictDetectionEnabled}
                    onMouseEnter={handlePopupEnter}
                    onMouseLeave={handleAssignmentLeave}
                    readOnly={true}
                />
            )}
        </div>,
        document.body
    );
};

export default AssignmentReadOnlyModal;
